import React, { forwardRef, useLayoutEffect } from 'react';
import { fontOf, highlightOf, chipSize, BLOCK_WIDTH, TEXT_MAX } from '../../../utils/portalLayout';

/**
 * The words block: the text typed on the Portal, and the window label under it, at the
 * layout's position, turned by its rotation (handoff §5.2). Each line of the text gets its own
 * highlight strip (box-decoration-break: clone). The label wears the same font, highlight,
 * colour and shadow as the words, so it changes with them.
 *
 * In the editor (`editable`) the text span is the input: it becomes contentEditable when
 * `editing`, the caret is cyan, and anything past 140 characters is cut on input. The span's
 * content is owned by the DOM then, never by React, so the caret survives every keystroke.
 *
 * `scale` = card width / 400 — the size is stored for a 400px card.
 */
export const wordsText = (el) => (el ? (el.innerText ?? el.textContent ?? '').replace(/\n$/, '') : '');

function caretToEnd(el) {
  try {
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(false);
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  } catch { /* no selection API */ }
}

const PortalWords = forwardRef(function PortalWords({
  text,
  chip = '',
  layout,
  scale = 1,
  dragging = false,
  interactive = false,
  editable = false,
  editing = false,
  spanRef = null,
  onInput = null,
  onPointerDown = null,
  onWheel = null,
  placeholder = 'Tap to type',
  children = null,
}, ref) {
  const font = fontOf(layout.font);
  const hl = highlightOf(layout.highlight);
  const size = layout.size * scale;
  const rot = layout.rot?.text || 0;
  const strip = {
    background: hl.bg,
    backgroundSize: hl.animated ? '200% 100%' : undefined,
    animation: hl.animated ? 'prcRingSlide 6s linear infinite' : undefined,
  };
  const type = {
    fontFamily: font.family,
    fontStyle: font.style,
    color: hl.fg,
    textShadow: hl.shadow,
  };

  // The editable span's content is set by hand: from the prop while nobody is typing, and
  // never while they are (that would move the caret).
  useLayoutEffect(() => {
    if (!editable) return;
    const el = spanRef?.current;
    if (!el) return;
    if (!editing && wordsText(el) !== (text || '')) el.textContent = text || '';
  }, [editable, editing, text, spanRef]);

  useLayoutEffect(() => {
    if (!editable) return;
    const el = spanRef?.current;
    if (!el) return;
    if (editing) {
      // plaintext-only where the engine has it (Chrome, Safari), true elsewhere (Firefox throws
      // on the value; jsdom ignores the property, so the attribute is what counts).
      const trySet = (v) => { try { el.contentEditable = v; } catch { return false; } return el.getAttribute('contenteditable') === v; };
      if (!trySet('plaintext-only') && !trySet('true')) el.setAttribute('contenteditable', 'true');
      el.focus();
      caretToEnd(el);
    } else {
      el.removeAttribute('contenteditable');
      try { el.contentEditable = 'false'; } catch { /* fine */ }
    }
  }, [editable, editing, spanRef]);

  const handleInput = (e) => {
    const el = e.currentTarget;
    let t = wordsText(el);
    if (t.length > TEXT_MAX) {
      t = t.slice(0, TEXT_MAX);
      el.textContent = t;
      caretToEnd(el);
    }
    onInput?.(t);
  };

  // Only the editor invites typing. A wordless Portal being WATCHED has nothing to tap — the
  // placeholder used to draw TAP TO TYPE over every one of them, on top of the event card.
  const showPlaceholder = editable && !editing && !text;

  return (
    <div
      ref={ref}
      data-drag="text"
      data-el="text"
      data-testid="portal-words"
      onPointerDown={onPointerDown || undefined}
      onWheel={onWheel || undefined}
      style={{
        position: 'absolute',
        left: `${layout.text.x}%`,
        top: `${layout.text.y}%`,
        width: `${BLOCK_WIDTH}%`,
        display: 'flex',
        flexDirection: 'column',
        gap: 10 * scale,
        padding: 8 * scale,
        boxSizing: 'border-box',
        outline: dragging ? '2px dashed #00FFFF' : editing ? '2px dashed rgba(0,255,255,.6)' : '2px dashed transparent',
        outlineOffset: 2,
        transform: rot ? `rotate(${rot}deg)` : undefined,
        cursor: interactive ? (editing ? 'text' : dragging ? 'grabbing' : 'grab') : undefined,
        touchAction: interactive ? (editing ? 'auto' : 'none') : undefined,
        zIndex: 5,
        userSelect: editing ? 'text' : 'none',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    >
      {children}
      <p
        style={{
          margin: 0,
          ...type,
          fontWeight: font.weight,
          textTransform: font.transform,
          letterSpacing: font.tracking,
          fontSize: size,
          lineHeight: 1.38,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {editable ? (
          <span
            ref={spanRef}
            className="prc-words"
            data-testid="portal-words-input"
            role={editing ? 'textbox' : undefined}
            aria-label={editing ? 'The words' : undefined}
            aria-multiline={editing ? 'true' : undefined}
            onInput={handleInput}
            suppressContentEditableWarning
            style={{ ...strip, padding: `${2 * scale}px ${10 * scale}px`, WebkitBoxDecorationBreak: 'clone', boxDecorationBreak: 'clone', display: !text && !editing ? 'none' : undefined }}
          />
        ) : (
          text ? (
            <span style={{ ...strip, padding: `${2 * scale}px ${10 * scale}px`, WebkitBoxDecorationBreak: 'clone', boxDecorationBreak: 'clone' }}>
              {text}
            </span>
          ) : null
        )}
        {showPlaceholder && (
          <span data-testid="portal-words-placeholder" style={{ ...strip, padding: `${2 * scale}px ${10 * scale}px`, opacity: 0.6 }}>{placeholder}</span>
        )}
      </p>
      {chip && (
        <span
          data-testid="portal-chip"
          style={{
            alignSelf: 'flex-start',
            ...strip,
            ...type,
            padding: `${4 * scale}px ${10 * scale}px`,
            fontSize: chipSize(layout.size) * scale,
            fontWeight: 700,
            letterSpacing: '0.24em',
            // A window across days is long; it wraps rather than running off the card.
            overflowWrap: 'anywhere',
          }}
        >
          {chip}
        </span>
      )}
    </div>
  );
});

export default PortalWords;
